/**
 * Zod Schemas for Search Listings API
 * Centralized location for /api/search/listings endpoint schemas
 */

import { z } from 'zod';

// =============================================================================
// REQUEST SCHEMAS
// =============================================================================

// Search request query parameters schema
export const SearchListingsQuerySchema = z.object({
  // Text search
  q: z.string().optional(),

  // Category filters
  categoryId: z.union([
    z.string(),
    z.array(z.string())
  ]).optional().transform((val) => {
    if (!val) return undefined;
    if (Array.isArray(val)) return val;
    return val.includes(',') ? val.split(',') : val;
  }),
  categoryGroupId: z.string().optional(),

  // Category exclusion filters
  excludeCategoryId: z.union([
    z.string(),
    z.array(z.string())
  ]).optional().transform((val) => {
    if (!val) return undefined;
    if (Array.isArray(val)) return val;
    return val.includes(',') ? val.split(',') : val;
  }),
  excludeCategoryGroupId: z.union([
    z.string(),
    z.array(z.string())
  ]).optional().transform((val) => {
    if (!val) return undefined;
    if (Array.isArray(val)) return val;
    return val.includes(',') ? val.split(',') : val;
  }),

  // Listing exclusion filter
  excludeListingId: z.string().optional(),

  // User exclusion filter
  excludeUserId: z.string().optional(),

  // Price filters
  minPrice: z.string().optional().transform((val) => val ? parseFloat(val) : undefined),
  maxPrice: z.string().optional().transform((val) => val ? parseFloat(val) : undefined),

  // Item condition filter (supports multiple conditions)
  itemCondition: z.union([
    z.string(),
    z.array(z.string())
  ]).optional().transform((val) => {
    if (!val) return undefined;
    if (Array.isArray(val)) return val;
    return val.split(',');
  }),

  // Boolean filters
  shippable: z.union([
    z.string(),
    z.boolean()
  ]).optional().transform((val) => {
    if (val === undefined) return undefined;
    if (typeof val === 'boolean') return val;
    return val === 'true' || val === '1';
  }),

  // Sold filter (include sold items)
  sold: z.union([
    z.string(),
    z.boolean()
  ]).optional().transform((val) => {
    if (val === undefined) return undefined;
    if (typeof val === 'boolean') return val;
    return val === 'true' || val === '1';
  }),

  // Geographic filters
  latitude: z.string().optional().transform((val) => {
    if (!val) return undefined;
    const num = parseFloat(val);
    if (num < -90 || num > 90) throw new Error('Latitude must be between -90 and 90');
    return num;
  }),
  longitude: z.string().optional().transform((val) => {
    if (!val) return undefined;
    const num = parseFloat(val);
    if (num < -180 || num > 180) throw new Error('Longitude must be between -180 and 180');
    return num;
  }),
  distance: z.string().optional().transform((val) => val ? parseFloat(val) : undefined),
  distanceStart: z.string().optional().transform((val) => val ? parseFloat(val) : undefined),

  // When true, auto-fill lat/lng from Vercel geo headers and sort by distance
  close: z.union([z.string(), z.boolean()]).optional().transform((val) => {
    if (val === undefined) return undefined;
    if (typeof val === 'boolean') return val;
    return val === 'true' || val === '1';
  }),

  // Membership filter
  membershipType: z.union([
    z.string(),
    z.array(z.string())
  ]).optional().transform((val) => {
    if (!val) return undefined;
    if (Array.isArray(val)) return val.map(v => parseInt(v, 10));
    if (val.includes(',')) return val.split(',').map(v => parseInt(v, 10));
    return parseInt(val, 10);
  }),

  // Featured filter (for partner-featured listings)
  featured: z.union([
    z.string(),
    z.boolean()
  ]).optional().transform((val) => {
    if (val === undefined) return undefined;
    if (typeof val === 'boolean') return val;
    return val === 'true' || val === '1';
  }),

  // ID filters
  userId: z.string().optional(),
  dataSourceId: z.string().optional().transform((val) => val ? parseInt(val, 10) : undefined),
  status: z.union([
    z.string(),
    z.array(z.string())
  ]).optional().transform((val) => {
    if (!val) return undefined;

    // Handle array input
    if (Array.isArray(val)) {
      const parsed = val
        .map(v => parseInt(v, 10))
        .filter(v => !isNaN(v));
      return parsed.length > 0 ? parsed : undefined;
    }

    // Handle comma-separated string
    if (val.includes(',')) {
      const parsed = val
        .split(',')
        .map(v => parseInt(v.trim(), 10))
        .filter(v => !isNaN(v));
      return parsed.length > 0 ? parsed : undefined;
    }

    // Handle single value
    const parsed = parseInt(val, 10);
    return !isNaN(parsed) ? parsed : undefined;
  }),

  // Sort order
  sortBy: z.string().optional(),

  // Pagination
  page: z.string().optional().default('1').transform((val) => parseInt(val, 10)),
  limit: z.string().optional().default('32').transform((val) => parseInt(val, 10)),
});

export type SearchListingsQuery = z.infer<typeof SearchListingsQuerySchema>;

// =============================================================================
// RESPONSE SCHEMAS
// =============================================================================

// User photo object
export const UserPhotoSchema = z.object({
  url: z.string(),
  thumbUrl: z.string(),
}).nullable();

// User object in listing response
export const SearchListingUserSchema = z.object({
  id: z.union([z.string(), z.number()]),
  firstName: z.string(),
  lastName: z.string(),
  trustLevel: z.number(),
  photos: UserPhotoSchema,
});

export type SearchListingUser = z.infer<typeof SearchListingUserSchema>;

// Photo object in listing response.
//
// Sized variants are what `getListingPhotos()` returns — the resize Lambda
// generates _160x160/_320x320/_640x640/_1280x1280 webp for every original.
// They stay optional because listings whose photos are still foreign-hosted
// (see METADATA_PHOTO_SOURCES) have only the one URL the origin gave us.
// `640x640Url` in particular is what listing-thumb-varied.tsx reaches for on
// tall cards, so anything building this object should populate it.
export const SearchListingPhotoSchema = z.object({
  url: z.string(),
  thumbUrl: z.string(),
  '160x160Url': z.string().optional(),
  '320x320Url': z.string().optional(),
  '640x640Url': z.string().optional(),
  '1280x1280Url': z.string().optional(),
});

export type SearchListingPhoto = z.infer<typeof SearchListingPhotoSchema>;

// Listing object in search response
export const SearchListingSchema = z.object({
  id: z.string(),
  title: z.string(),
  text: z.string().nullish(),
  price: z.number(),
  cost: z.number(),
  original_price: z.number().nullish(),
  slashedPrice: z.string().nullish(),
  percentOff: z.number().nullish(),
  couponId: z.number().nullish(),
  couponName: z.string().nullish(),
  couponDiscountAmount: z.number().nullish(),
  categoryId: z.number(),
  categoryGroupId: z.number(),
  categoryName: z.string().optional(),
  itemCondition: z.number().nullish(),
  status: z.number(),
  shippable: z.union([z.number(), z.boolean()]),
  locationText: z.string().nullish(),
  createdDate: z.string().nullish(),
  featuredDate: z.string().nullish(),
  tags: z.array(z.string()).optional().default([]),
  dataSourceId: z.number().nullish(),
  remote_url: z.string().nullish(),
  height: z.number().nullish(),
  width: z.number().nullish(),
  depth: z.number().nullish(),
  weight: z.number().nullish(),
  vin: z.string().nullish(),
  mileage: z.number().nullish(),
  hasPhoto: z.number(),
  photo: z.string().nullish(),
  photos: z.array(SearchListingPhotoSchema),
  user: SearchListingUserSchema.nullish(),
  distance: z.number().optional(),
  /** This seller's live listings of the same item, this one included (utils/listing-copies.ts). */
  copies: z.number().int().positive().optional(),
});

export type SearchListing = z.infer<typeof SearchListingSchema>;

// Success response
export const SearchListingsSuccessResponseSchema = z.object({
  success: z.literal(true),
  listings: z.array(SearchListingSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  hasMore: z.boolean(),
});

export type SearchListingsSuccessResponse = z.infer<typeof SearchListingsSuccessResponseSchema>;

// Error response
export const SearchListingsErrorResponseSchema = z.object({
  success: z.literal(false),
  error: z.string(),
});

export type SearchListingsErrorResponse = z.infer<typeof SearchListingsErrorResponseSchema>;

// Combined response using discriminated union
export const SearchListingsResponseSchema = z.discriminatedUnion('success', [
  SearchListingsSuccessResponseSchema,
  SearchListingsErrorResponseSchema,
]);

export type SearchListingsResponse = z.infer<typeof SearchListingsResponseSchema>;
